<template>
  <div class="pending-cdrs-tab">
    <!-- Filters -->
    <div class="filters-card">
      <div class="filters-grid">
        <!-- Period Filter -->
        <div class="filter-item">
          <label class="filter-label filter-label--active">{{ $t('pending-cdr.filters.period') }}</label>
          <v-select
            v-model="filters.period"
            :items="periodOptions"
            hide-details
            outlined
            dense
            @change="triggerSearch"
          />
        </div>

        <!-- Source Filter -->
        <div class="filter-item">
          <label class="filter-label">{{ $t('pending-cdr.filters.source') }}</label>
          <v-select
            v-model="filters.sources"
            :items="sourceOptions"
            hide-details
            outlined
            dense
            multiple
            clearable
            @change="triggerSearch"
          />
        </div>

        <!-- Issue Codes Filter -->
        <div class="filter-item">
          <label class="filter-label">{{ $t('pending-cdr.filters.issue-code') }}</label>
          <v-menu
            v-model="issueCodeMenu"
            :close-on-content-click="false"
            offset-y
            max-height="400"
          >
            <template v-slot:activator="{ on, attrs }">
              <div
                class="issue-code-trigger"
                v-bind="attrs"
                v-on="on"
              >
                <span class="issue-code-trigger-text">{{ issueCodeDisplayLabel }}</span>
                <v-icon small>{{ issueCodeMenu ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </div>
            </template>
            <div class="issue-code-dropdown" :class="{ 'issue-code-dropdown--with-selection': filters.issueCodes.length > 0 }">
              <div class="issue-code-main">
                <!-- Search -->
                <div class="issue-code-search">
                  <v-text-field
                    v-model="issueCodeSearch"
                    placeholder="Rechercher..."
                    hide-details
                    dense
                    outlined
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    autofocus
                  />
                </div>
                <!-- Actions -->
                <div class="issue-code-actions">
                  <button class="issue-code-action-btn" @click="selectAllIssueCodes">
                    {{ $t('select-all') }}
                  </button>
                  <button class="issue-code-action-btn" @click="deselectAllIssueCodes">
                    {{ $t('unselect-all') }}
                  </button>
                </div>
                <v-divider />
                <!-- List -->
                <div class="issue-code-list">
                  <div
                    v-for="option in filteredIssueCodeOptions"
                    :key="option.value"
                    class="issue-code-item"
                    @click="toggleIssueCode(option.value)"
                  >
                    <v-checkbox
                      :input-value="filters.issueCodes.includes(option.value)"
                      hide-details
                      dense
                      readonly
                      class="issue-code-checkbox"
                    />
                    <span class="issue-code-label">{{ option.text }}</span>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          x-small
                          v-bind="attrs"
                          v-on="on"
                          class="issue-code-exclude-btn"
                          @click.stop="excludeIssueCode(option.value)"
                        >
                          <v-icon x-small>mdi-eye-off-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Tout sauf celui-ci</span>
                    </v-tooltip>
                  </div>
                  <div v-if="filteredIssueCodeOptions.length === 0" class="issue-code-empty">
                    Aucun résultat
                  </div>
                </div>
              </div>
              <!-- Selection panel (right side) -->
              <div v-if="filters.issueCodes.length > 0" class="issue-code-selection">
                <div class="issue-code-selection-header">
                  {{ $t('pending-cdr.filters.selected') }} ({{ filters.issueCodes.length }})
                </div>
                <div class="issue-code-selection-list">
                  <v-chip
                    v-for="code in filters.issueCodes"
                    :key="code"
                    x-small
                    close
                    class="issue-code-chip"
                    @click:close="toggleIssueCode(code)"
                  >
                    {{ code }}
                  </v-chip>
                </div>
              </div>
            </div>
          </v-menu>
        </div>

        <!-- EVSE Search -->
        <div class="filter-item">
          <label class="filter-label">{{ $t('pending-cdr.filters.evse-search') }}</label>
          <v-text-field
            v-model="filters.evseId"
            :placeholder="$t('pending-cdr.filters.evse-placeholder')"
            hide-details
            outlined
            dense
            clearable
            append-icon="mdi-magnify"
            @keyup.enter="triggerSearch"
            @click:clear="triggerSearch"
          />
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="table-container">
      <v-data-table
        v-model="selectedItems"
        :headers="headers"
        :items="items"
        :loading="loading"
        :options.sync="options"
        :server-items-length="total"
        :footer-props="footerProps"
        show-select
        item-key="id"
        class="pending-table"
      >
        <!-- Date Column -->
        <template #item.createdAt="{ item }">
          <span class="date-cell">{{ formatDateTime(item.createdAt) }}</span>
        </template>

        <!-- Source Column -->
        <template #item.source="{ item }">
          <span class="source-badge" :class="'source-badge--' + item.source?.toLowerCase()">
            {{ formatSource(item.source) }}
          </span>
        </template>

        <!-- EVSE Column -->
        <template #item.evseId="{ item }">
          <div class="evse-cell-container">
            <router-link
              v-if="item.summary?.chargingPointId"
              :to="{ name: 'charging-points.detail', params: { id: item.summary.chargingPointId } }"
              target="_blank"
              class="evse-link"
            >
              <span class="evse-cell">{{ item.summary?.evseId || '-' }}</span>
              <v-icon x-small class="evse-link-icon">mdi-open-in-new</v-icon>
            </router-link>
            <span v-else class="evse-cell">{{ item.summary?.evseId || '-' }}</span>
            <span v-if="item.summary?.chargingPointName" class="evse-name">{{ item.summary.chargingPointName }}</span>
          </div>
        </template>

        <!-- Period Column -->
        <template #item.period="{ item }">
          <span v-if="item.summary?.startTime && item.summary?.stopTime" class="period-cell">
            {{ formatTime(item.summary.startTime) }} - {{ formatTime(item.summary.stopTime) }}
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- Energy Column -->
        <template #item.energy="{ item }">
          <span
            class="energy-cell"
            :class="{ 'energy-cell--negative': item.summary?.energy < 0 }"
          >
            {{ item.summary?.energy != null ? formatEnergy(item.summary.energy) : '-' }}
          </span>
        </template>

        <!-- Anomalies Column -->
        <template #item.validationErrors="{ item }">
          <div class="anomalies-cell">
            <template v-for="(error, index) in getSortedErrors(item.validationErrors)">
              <v-tooltip bottom :key="index">
                <template #activator="{ on, attrs }">
                  <span
                    v-bind="attrs"
                    v-on="on"
                    class="anomaly-badge"
                    :class="getAnomalyClass(error, index)"
                  >
                    <v-icon x-small class="mr-1" v-if="index === 0">{{ getAnomalyIcon(error) }}</v-icon>
                    {{ formatErrorCode(error.code) }}
                  </span>
                </template>
                <div>
                  <div class="font-weight-bold">{{ error.code }}</div>
                  <div v-if="error.message">{{ error.message }}</div>
                </div>
              </v-tooltip>
            </template>
          </div>
        </template>


        <!-- Actions Column -->
        <template #item.actions="{ item }">
          <v-btn
            v-if="item.status === 'invalid'"
            x-small
            depressed
            :color="isFirstRow(item) ? 'primary' : 'default'"
            :outlined="!isFirstRow(item)"
            :to="{ name: 'pending-cdrs.correction', params: { id: item.id } }"
            class="action-btn"
          >
            <v-icon left x-small>mdi-pencil</v-icon>
            {{ $t('pending-cdr.actions.correct') }}
          </v-btn>
        </template>
      </v-data-table>
    </div>

    <!-- Bulk Actions Bar -->
    <v-slide-y-reverse-transition>
      <div class="bulk-actions-bar" v-if="selectedItems.length > 0">
        <span class="bulk-count">
          {{ $t('pending-cdr.bulk.selected', { count: selectedItems.length }) }}
        </span>
        <v-btn
          color="error"
          small
          depressed
          @click="openBulkCancelDialog"
        >
          <v-icon left small>mdi-cancel</v-icon>
          {{ $t('pending-cdr.actions.cancel-selection') }}
        </v-btn>
      </div>
    </v-slide-y-reverse-transition>

    <!-- Bulk Cancel Dialog -->
    <v-dialog v-model="bulkCancelDialog" max-width="500">
      <v-card>
        <v-card-title>{{ $t('pending-cdr.actions.cancel-selection') }}</v-card-title>
        <v-card-text>
          <p class="mb-4">{{ $t('pending-cdr.messages.bulk-cancel-confirm', { count: selectedItems.length }) }}</p>
          <v-textarea
            v-model="cancelReason"
            :label="$t('pending-cdr.form.reason')"
            :hint="$t('pending-cdr.form.reason-hint')"
            persistent-hint
            outlined
            rows="3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text small @click="bulkCancelDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="error"
            small
            depressed
            :loading="bulkCancelling"
            :disabled="!cancelReason || cancelReason.length < 10"
            @click="executeBulkCancel"
          >
            {{ $t('confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PendingCdrRepository from '@repository/PendingCdrRepository'
import dayjs from 'dayjs'

export default {
  name: 'PendingCdrsTab',

  props: {
    backlog: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      loading: false,
      loadingIssueCodes: false,
      issueCodeOptions: [],
      issueCodeMenu: false,
      issueCodeSearch: '',
      items: [],
      total: 0,
      selectedItems: [],
      filters: {
        period: 'all',
        sources: [],
        evseId: null,
        issueCodes: [],
      },
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true],
      },
      bulkCancelDialog: false,
      cancelReason: '',
      bulkCancelling: false,
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('pending-cdr.columns.date'), value: 'createdAt', width: '160px' },
        { text: this.$t('pending-cdr.columns.source'), value: 'source', width: '100px', sortable: false },
        { text: this.$t('pending-cdr.columns.evse'), value: 'evseId', width: '200px', sortable: false },
        { text: this.$t('pending-cdr.columns.period'), value: 'period', sortable: false, width: '120px' },
        { text: this.$t('pending-cdr.columns.energy'), value: 'energy', sortable: false, width: '120px' },
        { text: this.$t('pending-cdr.columns.anomalies'), value: 'validationErrors', sortable: false },
        { text: this.$t('pending-cdr.columns.actions'), value: 'actions', sortable: false, width: '140px', align: 'right' },
      ]
    },

    footerProps() {
      return {
        'items-per-page-options': [5, 10, 20, 50],
        'items-per-page-text': this.$t('pending-cdr.pagination.rows-per-page'),
        'show-first-last-page': false,
      }
    },

    periodOptions() {
      return [
        { text: this.$t('pending-cdr.filters.period-all'), value: 'all' },
        { text: this.$t('pending-cdr.filters.period-24h'), value: '24h' },
        { text: this.$t('pending-cdr.filters.period-7d'), value: '7d' },
        { text: this.$t('pending-cdr.filters.period-older'), value: 'older' },
      ]
    },

    sourceOptions() {
      return [
        { text: 'OCPP', value: 'ocpp' },
        { text: 'Gireve', value: 'gireve_push' },
        { text: 'Hubject', value: 'hubject_push' },
      ]
    },

    issueCodeDisplayLabel() {
      const selected = this.filters.issueCodes
      if (!selected || selected.length === 0) return this.$t('pending-cdr.filters.all-issue-codes')
      if (selected.length === 1) return selected[0]
      return this.$t('pending-cdr.filters.issue-codes-count', { count: selected.length })
    },

    filteredIssueCodeOptions() {
      if (!this.issueCodeSearch) return this.issueCodeOptions
      const search = this.issueCodeSearch.toLowerCase()
      return this.issueCodeOptions.filter(o => o.text.toLowerCase().includes(search))
    },

  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.searchData()
      },
    },
  },

  mounted() {
    this.loadIssueCodes()
  },

  methods: {
    selectAllIssueCodes() {
      this.filters.issueCodes = this.filteredIssueCodeOptions.map(o => o.value)
      this.triggerSearch()
    },

    deselectAllIssueCodes() {
      this.filters.issueCodes = []
      this.triggerSearch()
    },

    toggleIssueCode(code) {
      const idx = this.filters.issueCodes.indexOf(code)
      if (idx >= 0) {
        this.filters.issueCodes.splice(idx, 1)
      } else {
        this.filters.issueCodes.push(code)
      }
      this.triggerSearch()
    },

    excludeIssueCode(code) {
      this.filters.issueCodes = this.issueCodeOptions
        .map(o => o.value)
        .filter(c => c !== code)
      this.triggerSearch()
    },

    async loadIssueCodes() {
      this.loadingIssueCodes = true
      try {
        const data = await PendingCdrRepository.getIssueCodes()
        const items = Array.isArray(data) ? data : (data.items || [])
        this.issueCodeOptions = items.map(item => ({
          text: item.code,
          value: item.code,
        }))
      } catch (e) {
        console.error('Failed to load issue codes:', e)
      } finally {
        this.loadingIssueCodes = false
      }
    },

    triggerSearch() {
      if (this.options.page > 1) {
        this.options.page = 1
      } else {
        this.searchData()
      }
    },

    sortByOldest() {
      this.options.sortBy = ['createdAt']
      this.options.sortDesc = [false]
    },

    applyFilters(query) {
      // Reset all filters first to avoid accumulation
      this.filters = {
        period: 'all',
        sources: [],
        evseId: null,
        issueCodes: [],
      }

      // Then apply only the filters from query
      if (query.sources) {
        this.filters.sources = Array.isArray(query.sources) ? query.sources : [query.sources]
      }
      if (query.period) {
        this.filters.period = query.period
      }
      if (query.evseId) {
        this.filters.evseId = query.evseId
      }
      if (query.issueCodes) {
        this.filters.issueCodes = Array.isArray(query.issueCodes) ? query.issueCodes : [query.issueCodes]
      }
      this.triggerSearch()
    },

    async searchData() {
      this.loading = true
      try {
        const { page, itemsPerPage, sortBy, sortDesc } = this.options
        const params = {
          offset: (page - 1) * itemsPerPage,
          limit: itemsPerPage,
          orderBy: sortBy?.[0] || 'createdAt',
          orderDirection: sortDesc?.[0] ? 'desc' : 'asc',
          statuses: ['invalid'],
        }

        // Period filter
        if (this.filters.period && this.filters.period !== 'all') {
          const now = dayjs()
          if (this.filters.period === '24h') {
            params.dateFrom = now.subtract(24, 'hour').toISOString()
          } else if (this.filters.period === '7d') {
            params.dateFrom = now.subtract(7, 'day').toISOString()
          } else if (this.filters.period === 'older') {
            params.dateTo = now.subtract(7, 'day').toISOString()
          }
        }

        // Source filter
        if (this.filters.sources?.length > 0) {
          params.sources = this.filters.sources
        }

        // EVSE filter
        if (this.filters.evseId) {
          params.evseId = this.filters.evseId
        }

        // Issue codes filter
        if (this.filters.issueCodes?.length > 0) {
          params.issueCodes = this.filters.issueCodes
        }

        const result = await PendingCdrRepository.search(params)
        this.items = result.items || []
        this.total = result.meta?.total || result.total || 0
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.loading = false
      }
    },

    isFirstRow(item) {
      return this.items[0]?.id === item.id
    },

    formatDateTime(dateString) {
      return dayjs(dateString).format('DD/MM/YYYY [à] HH[h]mm')
    },

    formatTime(dateString) {
      return dayjs(dateString).format('HH:mm')
    },

    formatEnergy(energyWh) {
      const kWh = energyWh / 1000
      return `${kWh.toFixed(2)} kWh`
    },

    formatSource(source) {
      const mapping = {
        'ocpp': 'OCPP',
        'gireve_push': 'Gireve',
        'gireve_pull': 'Gireve',
        'hubject_push': 'Hubject',
        'hubject_pull': 'Hubject',
      }
      return mapping[source?.toLowerCase()] || source
    },

    formatErrorCode(code) {
      return code?.replace('CDR_', '').replace(/_/g, ' ') || code
    },

    getSortedErrors(errors) {
      if (!errors || errors.length === 0) return []
      return [...errors].sort((a, b) => {
        if (a.severity === 'blocking' && b.severity !== 'blocking') return -1
        if (a.severity !== 'blocking' && b.severity === 'blocking') return 1
        return 0
      })
    },

    getAnomalyClass(error, index) {
      const classes = []
      if (index === 0) {
        classes.push('anomaly-badge--primary')
        const colorClass = this.getAnomalyColorClass(error.code)
        classes.push(colorClass)
      } else {
        classes.push('anomaly-badge--secondary')
      }
      return classes
    },

    getAnomalyColorClass(code) {
      const colorMap = {
        'CDR_METER_INVERSION': 'anomaly-badge--red',
        'CDR_NEGATIVE_ENERGY': 'anomaly-badge--red',
        'CDR_METER_DISCONTINUITY': 'anomaly-badge--purple',
        'CDR_MISSING_TARIFF': 'anomaly-badge--yellow',
        'CDR_MISSING_EVSE': 'anomaly-badge--orange',
        'CDR_INVALID_DATES': 'anomaly-badge--orange',
        'CDR_DUPLICATE': 'anomaly-badge--orange',
      }
      return colorMap[code] || 'anomaly-badge--red'
    },

    getAnomalyIcon(error) {
      const icons = {
        'CDR_METER_INVERSION': 'mdi-flash',
        'CDR_NEGATIVE_ENERGY': 'mdi-trending-down',
        'CDR_MISSING_TARIFF': 'mdi-currency-eur',
        'CDR_METER_DISCONTINUITY': 'mdi-speedometer',
        'CDR_MISSING_EVSE': 'mdi-ev-station',
        'CDR_INVALID_DATES': 'mdi-calendar-alert',
        'CDR_DUPLICATE': 'mdi-content-copy',
      }
      return icons[error.code] || 'mdi-alert'
    },

    openBulkCancelDialog() {
      this.cancelReason = ''
      this.bulkCancelDialog = true
    },

    async executeBulkCancel() {
      if (!this.cancelReason || this.cancelReason.length < 10) return

      this.bulkCancelling = true
      try {
        const ids = this.selectedItems.map(item => item.id)
        await PendingCdrRepository.bulkCancel(ids, this.cancelReason)
        this.notifySuccess(this.$t('pending-cdr.messages.bulk-cancel-success', { count: ids.length }))
        this.selectedItems = []
        this.bulkCancelDialog = false
        this.$emit('cancelled')
        this.searchData()
      } catch (error) {
        this.notifyError(error)
      } finally {
        this.bulkCancelling = false
      }
    },

    // Public method for external refresh (polling)
    refresh() {
      return this.searchData()
    },
  },
}
</script>

<style lang="scss" scoped>
$primary: #EA4335;
$border-color: #E5E7EB;
$text-muted: #6B7280;

.pending-cdrs-tab {
  padding: 24px;
}

// Filters
.filters-card {
  background: white;
  border-radius: 8px;
  border: 1px solid $border-color;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.filters-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.filter-item {
  position: relative;
}

.filter-label {
  position: absolute;
  top: -8px;
  left: 12px;
  background: white;
  padding: 0 4px;
  font-size: 12px;
  color: $text-muted;
  font-weight: 500;
  z-index: 1;

  &--active {
    color: $primary;
  }
}

// Table
.table-container {
  background: white;
  border-radius: 8px;
  border: 1px solid $border-color;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.pending-table {
  ::v-deep {
    .v-data-table-header {
      background: #F9FAFB;

      th {
        font-size: 12px !important;
        font-weight: 500 !important;
        text-transform: none !important;
        letter-spacing: normal;
        color: $text-muted !important;
        border-bottom: 1px solid $border-color !important;
        padding: 14px 16px !important;
        white-space: nowrap;

        .v-data-table-header__icon {
          margin-left: 4px;
          opacity: 0.5;
        }
      }
    }

    tbody tr {
      &:hover {
        background: #F9FAFB !important;
      }

      td {
        font-size: 14px;
        padding: 12px 16px !important;
        border-bottom: 1px solid $border-color !important;
      }
    }

    .v-data-footer {
      border-top: 1px solid $border-color;
      font-size: 13px;
    }
  }
}

.date-cell {
  font-weight: 500;
  color: #374151;
}

.source-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid;

  &--ocpp {
    background: #DBEAFE;
    color: #1E40AF;
    border-color: #BFDBFE;
  }

  &--gireve_push,
  &--gireve_pull {
    background: #D1FAE5;
    color: #065F46;
    border-color: #A7F3D0;
  }

  &--hubject_push,
  &--hubject_pull {
    background: #FFEDD5;
    color: #9A3412;
    border-color: #FED7AA;
  }
}

.evse-cell-container {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.evse-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    .evse-cell {
      color: $primary;
    }
    .evse-link-icon {
      opacity: 1;
    }
  }
}

.evse-link-icon {
  opacity: 0.4;
  color: $text-muted;
  transition: opacity 0.15s ease;
}

.evse-cell {
  font-family: ui-monospace, monospace;
  font-weight: 700;
  font-size: 13px;
  color: #111827;
  transition: color 0.15s ease;
}

.evse-name {
  font-size: 11px;
  color: $text-muted;
  font-weight: 400;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.period-cell {
  color: $text-muted;
}

.energy-cell {
  font-weight: 500;

  &--negative {
    color: $primary;
    font-weight: 700;
  }
}

.anomalies-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.anomaly-badge {
  display: inline-flex;
  align-items: center;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;

  &--primary {
    font-weight: 700;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &--secondary {
    opacity: 0.75;
    font-size: 10px;
    background: #F3F4F6;
    color: #4B5563;
    border: 1px solid #E5E7EB;
  }

  &--red {
    background: #FEE2E2;
    color: #991B1B;
    border: 1px solid #FECACA;
  }

  &--orange {
    background: #FFEDD5;
    color: #9A3412;
    border: 1px solid #FED7AA;
  }

  &--yellow {
    background: #FEF3C7;
    color: #92400E;
    border: 1px solid #FDE68A;
  }

  &--purple {
    background: #F3E8FF;
    color: #6B21A8;
    border: 1px solid #E9D5FF;
  }
}


.action-btn {
  text-transform: none;
  font-weight: 600;
}

.text-muted {
  color: $text-muted;
}

// Bulk Actions Bar
.bulk-actions-bar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: #1F2937;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
}

.bulk-count {
  font-size: 14px;
  font-weight: 500;
}

// Issue Code Dropdown
.issue-code-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  border: 1px solid #a8a8a8;
  border-radius: 4px;
  cursor: pointer;
  background: white;
  min-height: 40px;
  transition: border-color 0.2s;

  &:hover {
    border-color: #374151;
  }
}

.issue-code-trigger-text {
  font-size: 14px;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.issue-code-dropdown {
  background: white;
  min-width: 320px;
  display: flex;

  &--with-selection {
    min-width: 500px;
  }
}

.issue-code-main {
  flex: 1;
  min-width: 0;
}

.issue-code-search {
  padding: 8px;
}

.issue-code-actions {
  display: flex;
  gap: 8px;
  padding: 4px 8px 8px;
}

.issue-code-action-btn {
  font-size: 12px;
  color: $primary;
  background: none;
  border: none;
  cursor: pointer;
  font-weight: 500;
  padding: 2px 4px;
  border-radius: 4px;

  &:hover {
    background: rgba($primary, 0.08);
  }
}

.issue-code-list {
  max-height: 240px;
  overflow-y: auto;
}

.issue-code-item {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #F9FAFB;

    .issue-code-exclude-btn {
      opacity: 1;
    }
  }
}

.issue-code-checkbox {
  flex-shrink: 0;
  margin: 0;
  padding: 0;

  ::v-deep .v-input--selection-controls__input {
    margin-right: 4px;
  }
}

.issue-code-label {
  font-size: 13px;
  font-family: ui-monospace, monospace;
  color: #374151;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.issue-code-exclude-btn {
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.issue-code-empty {
  padding: 16px;
  text-align: center;
  font-size: 13px;
  color: $text-muted;
}

.issue-code-selection {
  width: 180px;
  border-left: 1px solid $border-color;
  background: #F9FAFB;
  display: flex;
  flex-direction: column;
}

.issue-code-selection-header {
  font-size: 11px;
  font-weight: 600;
  color: $text-muted;
  padding: 10px 10px 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.issue-code-selection-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 0 10px 10px;
  overflow-y: auto;
  max-height: 280px;
  align-content: flex-start;
}

.issue-code-chip {
  font-size: 10px !important;
  font-family: ui-monospace, monospace;
  height: 20px !important;
}
</style>
